import { useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { API_URL } from "../../api/api";

// "Approving Manager" field for every Apply-for-Leave form.
//
// Options come from GET /api/leaves/managers — the employee's assigned manager
// plus the managers of the projects they are on (the same list the
// regularisation form offers). The picked manager is the one who decides the
// request. Drop-in for the forms' shared `.form-item` markup: it emits a
// normal change event for `name="manager_id"`, so each form's existing
// handleChange stores it.
//
// Required whenever there is someone to pick — the server enforces the same.
// With no candidates it says so, and the request goes to the Admin/HR
// approvers as before.
const LeaveManagerSelect = ({ value, onChange }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("token");
    axios
      .get(`${API_URL}/api/leaves/managers`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setOptions(list);
        // Only one person can approve — pick them rather than make the
        // employee open a one-item dropdown.
        if (list.length === 1 && !value) {
          onChange({ target: { name: "manager_id", value: String(list[0].id) } });
        }
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Load once per form open; `value`/`onChange` are only read for the
    // single-option default above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasOptions = options.length > 0;

  return (
    <div className={`form-item${hasOptions ? " required" : ""}`}>
      <label>Approving Manager</label>
      <select
        name="manager_id"
        value={value || ""}
        onChange={onChange}
        required={hasOptions}
        disabled={loading || !hasOptions}
      >
        <option value="">
          {loading
            ? "Loading managers…"
            : hasOptions
              ? "Select manager"
              : "No manager assigned — HR / Admin will review"}
        </option>
        {options.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
            {m.source === "project" && m.project_name
              ? ` (Project manager · ${m.project_name})`
              : m.source === "assigned"
                ? " (Reporting manager)"
                : ""}
          </option>
        ))}
      </select>
    </div>
  );
};

LeaveManagerSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
};

export default LeaveManagerSelect;
